import { db } from "@db/db";
import type { ImportRecord } from "@db/imports";
import { GNOMUX_ROOT_ID } from "@db/objects";
import { putObjectPlaceholder } from "@db/uploads";
import { getErrorMessage } from "@lib/errors";
import type { SequenceKind } from "@lib/genetics";
import { guardStorageQuota } from "@lib/storage";
import { useImportStore } from "./importStore";
import type { WorkerRequest, WorkerResponse } from "./types";

type UploadOptions = {
  parentId?: string;
  ownerId: string;
};

type UseUploadsResult = {
  upload: (file: File, options: UploadOptions) => Promise<void>;
  uploadMultiple: (files: File[], options: UploadOptions) => Promise<void>;
};

let worker: Worker | null = null;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL("./uploadWorker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = handleWorkerMessage;
    worker.onerror = handleWorkerError;
  }
  return worker;
}

function handleWorkerMessage(e: MessageEvent<WorkerResponse>): void {
  const msg = e.data;
  const importStore = useImportStore.getState();

  switch (msg.type) {
    case "progress":
      importStore.update(msg.importId, {
        status: msg.status as "staging" | "detecting" | "ingesting",
        progress: msg.progress,
      });
      break;

    case "sizeWarning":
      importStore.update(msg.importId, {
        status: "sizeWarning",
        progress: 20,
        decompressedSize: msg.decompressedSize,
      });
      break;

    case "analyzed":
      importStore.update(msg.importId, {
        status: "analyzed",
        progress: 30,
        analysis: msg.analysis,
      });
      break;

    case "complete":
      importStore.update(msg.importId, {
        status: "complete",
        progress: 100,
        completedAt: Date.now(),
        objectId: msg.placeholderId,
      });
      break;

    case "error":
      importStore.update(msg.importId, {
        status: "error",
        error: msg.error,
        objectId: msg.placeholderId,
      });
      break;
  }
}

function handleWorkerError(event: ErrorEvent): void {
  const message = getErrorMessage(event.error, "Upload worker crashed");
  const importStore = useImportStore.getState();

  for (const item of importStore.imports) {
    if (item.status !== "complete" && item.status !== "error") {
      importStore.update(item.id, { status: "error", error: message });
    }
  }

  worker?.terminate();
  worker = null;
}

export function acceptSizeWarning(importId: string): void {
  getWorker().postMessage({
    type: "acceptSizeWarning",
    importId,
  } satisfies WorkerRequest);
}

export function confirmImport(
  importId: string,
  sequenceKind?: SequenceKind,
): void {
  getWorker().postMessage({
    type: "continue",
    importId,
    sequenceKind,
  } satisfies WorkerRequest);
}

export function cancelImport(importId: string): void {
  getWorker().postMessage({
    type: "cancel",
    importId,
  } satisfies WorkerRequest);

  useImportStore.getState().remove(importId);
}

/**
 * Resume incomplete imports that have staged files in OPFS.
 * Called on app startup to recover from tab crashes.
 */
export async function resumeIncompleteImports(): Promise<void> {
  const records: ImportRecord[] = await db.imports
    .where("status")
    .anyOf(["staging", "analyzing", "analyzed", "ingesting"])
    .toArray();

  const importStore = useImportStore.getState();

  for (const record of records) {
    importStore.addResumed({
      id: record.id,
      fileName: record.fileName,
      parentId: record.parentId,
    });

    getWorker().postMessage({
      type: "resume",
      importId: record.id,
    } satisfies WorkerRequest);
  }
}

export function useUploads(): UseUploadsResult {
  const importStore = useImportStore();

  async function upload(file: File, options: UploadOptions): Promise<void> {
    const importId = crypto.randomUUID();
    const parentId = options.parentId ?? GNOMUX_ROOT_ID;
    const { ownerId } = options;

    const placeholderId = crypto.randomUUID();
    await putObjectPlaceholder({
      id: placeholderId,
      name: file.name,
      parent: parentId,
      createdAt: new Date(),
      ownerId,
      status: "importing",
    });

    importStore.add({ id: importId, fileName: file.name, file, parentId });

    getWorker().postMessage({
      type: "upload",
      file,
      importId,
      placeholderId,
      parentId,
      fileName: file.name,
      ownerId,
    } satisfies WorkerRequest);
  }

  async function uploadMultiple(
    files: File[],
    options: UploadOptions,
  ): Promise<void> {
    if (await guardStorageQuota()) return;

    for (const file of files) {
      await upload(file, options);
    }
  }

  return { upload, uploadMultiple };
}
